import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/common'
import { useAuthStore } from '../../store'
import { TipoLocale, Provincia } from '../../types'
import api from '../../services/api'

const TIPI_LOCALE: TipoLocale[] = ['ristorante', 'trattoria', 'agriturismo', 'osteria', 'pizzeria', 'pub']
const PROVINCE: Provincia[] = ['AN', 'PU', 'MC', 'FM', 'AP']

/**
 * Pagina per segnalare un nuovo locale
 */
export default function Segnala() {
  const { utente } = useAuthStore()
  const isAuthenticated = utente !== null
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    nomeLocale: '',
    tipoLocale: '' as TipoLocale | '',
    comune: '',
    provincia: '' as Provincia | '',
    indirizzo: '',
    descrizione: '',
    specialita: '',
    perche: '',
    email: '', // Solo per utenti non loggati
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await api.post('/segnalazioni', {
        nomeLocale: formData.nomeLocale,
        tipoLocale: formData.tipoLocale || null,
        comune: formData.comune || null,
        provincia: formData.provincia || null,
        indirizzoApprossimativo: formData.indirizzo || null,
        descrizione: formData.descrizione || null,
        specialitaSuggerite: formData.specialita || null,
        comeScoperto: formData.perche || null,
        emailContatto: !isAuthenticated ? formData.email : null,
      })
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Errore nell\'invio della segnalazione')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="font-display text-3xl text-white mb-4">Grazie per la segnalazione!</h1>
          <p className="text-gray-400 mb-8">
            Il team Conero Garage valuterà il locale che hai segnalato.
            Se verrà approvato, lo troverai presto nella nostra guida!
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/locali">
              <Button variant="primary">Esplora i locali</Button>
            </Link>
            <Button variant="secondary" onClick={() => { setSuccess(false); setFormData({ nomeLocale: '', tipoLocale: '', comune: '', provincia: '', indirizzo: '', descrizione: '', specialita: '', perche: '', email: '' }) }}>
              Segnala un altro
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-display text-4xl text-white mb-4">
            Segnala un <span className="text-gold">Locale</span>
          </h1>
          <p className="text-gray-400">
            Conosci un posto dove si mangia bene? Segnalacelo!
            Il team Conero Garage lo visiterà e valuterà se inserirlo nella guida.
          </p>
        </div>

        {/* Info box */}
        <div className="bg-gold/10 border border-gold/30 rounded-xl p-6 mb-8">
          <div className="flex gap-4">
            <div className="w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-gold font-medium mb-1">Come funziona?</h3>
              <p className="text-gray-400 text-sm">
                Riceveremo la tua segnalazione e organizzeremo una visita durante una delle nostre uscite.
                Se il locale supera la prova, riceverà il bollino "Conero Garage Approved"!
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Nome locale */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nome del locale *
            </label>
            <input
              type="text"
              name="nomeLocale"
              value={formData.nomeLocale}
              onChange={handleChange}
              required
              placeholder="Es: Trattoria da Mario"
              className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold"
            />
          </div>

          {/* Tipo e Provincia */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tipo di locale
              </label>
              <select
                name="tipoLocale"
                value={formData.tipoLocale}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              >
                <option value="">Seleziona...</option>
                {TIPI_LOCALE.map(tipo => (
                  <option key={tipo} value={tipo} className="capitalize">{tipo}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Provincia
              </label>
              <select
                name="provincia"
                value={formData.provincia}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              >
                <option value="">Seleziona...</option>
                {PROVINCE.map(prov => (
                  <option key={prov} value={prov}>{prov}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Comune e Indirizzo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Comune
              </label>
              <input
                type="text"
                name="comune"
                value={formData.comune}
                onChange={handleChange}
                placeholder="Es: Sirolo"
                className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Indirizzo (se lo conosci)
              </label>
              <input
                type="text"
                name="indirizzo"
                value={formData.indirizzo}
                onChange={handleChange}
                placeholder="Es: Via Roma 15"
                className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold"
              />
            </div>
          </div>

          {/* Specialità */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Specialità del locale
            </label>
            <input
              type="text"
              name="specialita"
              value={formData.specialita}
              onChange={handleChange}
              placeholder="Es: Vincisgrassi, brodetto, olive ascolane"
              className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold"
            />
          </div>

          {/* Descrizione */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Racconta qualcosa sul locale
            </label>
            <textarea
              name="descrizione"
              value={formData.descrizione}
              onChange={handleChange}
              rows={3}
              placeholder="Cosa ti è piaciuto? Ambiente, servizio, qualità..."
              className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold resize-none"
            />
          </div>

          {/* Come l'hai scoperto */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Come hai scoperto questo locale?
            </label>
            <input
              type="text"
              name="perche"
              value={formData.perche}
              onChange={handleChange}
              placeholder="Es: Passaparola, ci sono capitato per caso, me l'ha consigliato un amico..."
              className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold"
            />
          </div>

          {/* Email per utenti non loggati - OBBLIGATORIA */}
          {!isAuthenticated && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                La tua email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Per ricevere aggiornamenti sulla segnalazione"
                className="w-full px-4 py-3 bg-night-light border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ti avviseremo quando valuteremo il locale
              </p>
            </div>
          )}

          {/* Info utente loggato */}
          {isAuthenticated && utente && (
            <div className="bg-night-light border border-gray-700 rounded-lg p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                <span className="text-gold font-medium">{utente.nome.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <p className="text-white text-sm">{utente.nome}</p>
                <p className="text-gray-500 text-xs">La segnalazione sarà associata al tuo account</p>
              </div>
            </div>
          )}

          {/* Submit */}
          <Button type="submit" variant="primary" className="w-full" disabled={loading}>
            {loading ? 'Invio in corso...' : 'Invia Segnalazione'}
          </Button>
        </form>
      </div>
    </div>
  )
}
